/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import { useIntl } from 'react-intl';
import { z } from 'zod';

import {
  useChannelRefinementValidators,
  validateEndDateIsAfterStartDate,
} from './channel-refinements';

/**
 * Hook to create the validation schema for channel configuration
 * including budget allocation, dates, and per-opening budgets
 */
export function useChannelSchema() {
  const intl = useIntl();

  const messages = {
    budgetRequired: intl.formatMessage({
      defaultMessage: 'Budget is required',
      description: 'Budget input required error message',
    }),
    endDateRequired: intl.formatMessage({
      defaultMessage: 'End date is required',
      description: 'End date input required error message',
    }),
    endDateMustBeLaterThanStartDate: intl.formatMessage({
      defaultMessage: 'End date must be later than start date',
      description: 'End date input validation error message',
    }),
  };

  const {
    validatePerFunnelMinimumBudgets,
    validateOpeningBudgetsTotalMatchesChannelBudget,
    validateEvenAllocationMinimumBudget,
  } = useChannelRefinementValidators();
  return z.record(
    z.string(),
    z
      .object({
        startsAt: z.string(),
        endAt: z
          .string()
          .nullable()
          .refine(value => value !== null && value !== '', {
            message: messages.endDateRequired,
          }),
        budgetType: z
          .enum(['daily', 'weekly', 'monthly', 'lifetime'])
          .nullable(),
        openingBudgets: z.record(
          z.string(),
          z
            .number({
              message: messages.budgetRequired,
            })
            .min(1, {
              message: messages.budgetRequired,
            }),
        ),
        budgetAllocation: z.enum(['evenly', 'per-funnel']),
        channelBudget: z
          .number({
            message: messages.budgetRequired,
          })
          .min(1, {
            message: messages.budgetRequired,
          })
          .refine(value => value > 0, {
            message: messages.budgetRequired,
          }),
        isExpanded: z.boolean(),
        endAtType: z.enum(['fixed', 'target']).nullable(),
        slug: z.string(),
      })
      // Validate that the opening budgets are greater than the minimum required budget for per funnel allocation
      .superRefine((data, ctx) => {
        validatePerFunnelMinimumBudgets(data, ctx);
      })
      // Validate that the end date is greater than the start date
      .refine(data => validateEndDateIsAfterStartDate(data), {
        message: messages.endDateMustBeLaterThanStartDate,
        path: ['endAt'],
      })
      // Validate that the total opening budgets match the channel budget (with tolerance for rounding)
      .superRefine((data, ctx) => {
        validateOpeningBudgetsTotalMatchesChannelBudget(data, ctx);
      })
      // Validate that the channel budget is greater than the minimum required budget
      .superRefine((data, ctx) => {
        validateEvenAllocationMinimumBudget(data, ctx);
      }),
  );
}
